/**
 * Anonymous, batched telemetry.
 *
 * No account, no PII. A random `anon_id` lives in localStorage so repeat visits
 * from the same browser can be stitched together; nothing identifies a person.
 * Search text is sent ONLY when a search returned zero results — that is the
 * high-value, low-risk cut: it tells us exactly what the database is missing
 * without logging what people type generally.
 *
 * The whole module no-ops unless VITE_TELEMETRY_URL is set, so the app ships
 * and works fully with telemetry switched off.
 *
 * The two numbers this exists to produce are derived server-side:
 *
 *   time_to_first_schedule_add
 *     first `schedule_add` minus the first `session_start` for that anon_id.
 *
 *   time_to_locked_itinerary
 *     the moment an anon_id first holds >= 1 scheduled event on >= 3 distinct
 *     days, confirmed by 24h with no further schedule mutation.
 *
 * See handoff/api-contract.md for the wire format.
 */

const ENDPOINT = import.meta.env.VITE_TELEMETRY_URL as string | undefined;
const APP_VERSION = '0.1.0';
const ANON_KEY = 'mcw.anon_id';
const FLUSH_MS = 10_000;
const MAX_BATCH = 40;

export type Surface = 'card' | 'detail' | 'alternative' | 'schedule';

export type Signal =
  | { t: 'session_start'; ts: string }
  | { t: 'view_change'; ts: string; to: 'explore' | 'schedule' }
  | { t: 'search'; ts: string; len: number; results: number; query?: string }
  | {
      t: 'filter_change';
      ts: string;
      facet: 'day' | 'time' | 'intent' | 'topic' | 'other' | 'reset';
      value: string;
      on: boolean;
      active: number;
      results: number;
    }
  | { t: 'event_open'; ts: string; id: string; from: Surface; rank: number }
  | { t: 'schedule_add'; ts: string; id: string; from: Surface }
  | { t: 'schedule_remove'; ts: string; id: string; from: Surface }
  | { t: 'save_add'; ts: string; id: string; from: Surface }
  | { t: 'save_remove'; ts: string; id: string; from: Surface }
  | { t: 'report_open'; ts: string; id: string }
  | { t: 'conflict_shown'; ts: string; ids: [string, string]; overlap_min: number }
  | { t: 'alternative_taken'; ts: string; replaced: string; chosen: string; score: number }
  /** The move to an account and its syncing. Counts and statuses only, never ids. */
  | {
      t: 'account_sync';
      ts: string;
      step: 'load' | 'import' | 'sweep' | 'flush';
      outcome: 'done' | 'retry' | 'rejected' | 'unverified' | 'dropped';
      status?: number;
      missing?: number;
      unresolved?: number;
      count?: number;
    };

export type SignalInput =
  | Omit<Extract<Signal, { t: 'session_start' }>, 'ts'>
  | Omit<Extract<Signal, { t: 'view_change' }>, 'ts'>
  | Omit<Extract<Signal, { t: 'search' }>, 'ts'>
  | Omit<Extract<Signal, { t: 'filter_change' }>, 'ts'>
  | Omit<Extract<Signal, { t: 'event_open' }>, 'ts'>
  | Omit<Extract<Signal, { t: 'schedule_add' }>, 'ts'>
  | Omit<Extract<Signal, { t: 'schedule_remove' }>, 'ts'>
  | Omit<Extract<Signal, { t: 'save_add' }>, 'ts'>
  | Omit<Extract<Signal, { t: 'save_remove' }>, 'ts'>
  | Omit<Extract<Signal, { t: 'report_open' }>, 'ts'>
  | Omit<Extract<Signal, { t: 'conflict_shown' }>, 'ts'>
  | Omit<Extract<Signal, { t: 'alternative_taken' }>, 'ts'>
  | Omit<Extract<Signal, { t: 'account_sync' }>, 'ts'>;

function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function readAnonId(): string {
  try {
    const existing = localStorage.getItem(ANON_KEY);
    if (existing) return existing;
    const fresh = uuid();
    localStorage.setItem(ANON_KEY, fresh);
    return fresh;
  } catch {
    // Private browsing, or site data blocked. Still usable, just not stitched.
    return uuid();
  }
}

function viewportClass(): 'mobile' | 'tablet' | 'desktop' {
  if (typeof window === 'undefined') return 'desktop';
  if (window.innerWidth < 768) return 'mobile';
  if (window.innerWidth < 1100) return 'tablet';
  return 'desktop';
}

const enabled = Boolean(ENDPOINT);
const sessionId = uuid();
let anonId = '';
let queue: Signal[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;

function envelope(events: Signal[]) {
  return {
    anon_id: anonId,
    session_id: sessionId,
    app_version: APP_VERSION,
    viewport: viewportClass(),
    sent_at: new Date().toISOString(),
    events,
  };
}

function flush(useBeacon = false): void {
  if (!enabled || queue.length === 0) return;
  const batch = queue;
  queue = [];
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }

  const body = JSON.stringify(envelope(batch));
  try {
    if (useBeacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
      navigator.sendBeacon(ENDPOINT as string, new Blob([body], { type: 'application/json' }));
      return;
    }
    void fetch(ENDPOINT as string, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {
      /* Telemetry must never surface an error to the user. */
    });
  } catch {
    /* ignore */
  }
}

export function track(signal: SignalInput): void {
  if (!enabled) return;
  queue.push({ ...signal, ts: new Date().toISOString() } as Signal);
  if (queue.length >= MAX_BATCH) {
    flush();
    return;
  }
  if (!timer) timer = setTimeout(() => flush(), FLUSH_MS);
}

export function initTelemetry(): void {
  if (!enabled) return;
  anonId = readAnonId();
  track({ t: 'session_start' });
  // `pagehide` fires reliably on mobile Safari where `beforeunload` does not.
  window.addEventListener('pagehide', () => flush(true));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush(true);
  });
}

export const telemetryEnabled = enabled;
